/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react';
import { useKeycloak } from '@react-keycloak/web';
import { CopyOutlined, LogoutOutlined } from '@ant-design/icons';

import { DropdownMenu } from '@app/components/header/Header.styles';
import { notificationController } from '@app/controllers/notificationController';
import { useResponsive } from '@app/hooks/useResponsive';
import * as S from './ProfileOverlay.styles';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

export const ProfileOverlay: React.FC = (props) => {
  const { t } = useTranslation();
  const { keycloak } = useKeycloak();
  const { mobileOnly } = useResponsive();
  const navigate = useNavigate();

  const tokenParsed = keycloak.tokenParsed || {};
  const { name, preferred_username, email, resource_access } =
    tokenParsed as any;

  const roles: string[] = resource_access?.['synergy']?.roles || [];
  const filteredRoles = roles.filter(
    (r) => r !== 'offline_access' && !r.startsWith('default-roles')
  );

  const copyToken = () => {
    const token = keycloak.token || '';

    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard
        .writeText(token)
        .then(() =>
          notificationController.success({
            message: t('common.success'),
            description: t('profile.token_copied'),
          })
        )
        .catch(() => fallbackCopy());
    } else {
      fallbackCopy();
    }

    function fallbackCopy() {
      try {
        const textarea = document.createElement('textarea');
        textarea.value = token;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
        notificationController.success({
          message: t('common.success'),
          description: t('profile.token_copied'),
        });
      } catch {
        notificationController.error({
          message: t('common.error'),
          description: t('profile.token_copy_error'),
        });
      }
    }
  };

  const menuItems = [
    {
      key: 'profile',
      label: (
        <S.ProfileBlock>
          <S.Name>{name}</S.Name>

          {!mobileOnly && (
            <>
              <S.Email>{email}</S.Email>
              <S.Username>
                {t('profile.user')}: {preferred_username}
              </S.Username>

              {filteredRoles.length > 0 && (
                <S.Roles>
                  {filteredRoles.map((r) => (
                    <S.RoleTag key={r} color="blue">
                      {r}
                    </S.RoleTag>
                  ))}
                </S.Roles>
              )}
            </>
          )}
        </S.ProfileBlock>
      ),
      onClick: () => navigate('/profile'),
    },
    {
      key: 'copy-token',
      icon: <CopyOutlined />,
      label: t('profile.copy_token'),
      onClick: copyToken,
    },
    { type: 'divider' as const },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      danger: true,
      label: t('header.logout'),
      onClick: () => keycloak.logout({ redirectUri: window.location.origin }),
    },
  ];

  return <DropdownMenu selectable={false} items={menuItems} {...props} />;
};
